import { useState, useEffect } from "react";
import {
  Plus, Edit2, Trash2, Loader2, X, CheckCircle, AlertCircle,
  Plug, RefreshCw, Eye, RotateCcw, Zap,
} from "lucide-react";
import {
  integracaoService,
  Integracao,
  IntegracaoLog,
} from "../../../services/integracaoService";

const TIPO_LABELS: Record<string, string> = {
  finance: "Finance (contas a receber)",
  zooy: "Zooy OS (resumo de faturas)",
  webhook: "Webhook genérico",
};

export function IntegrationsManagement() {
  const [integracoes, setIntegracoes] = useState<Integracao[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Integracao | null>(null);
  const [logsFor, setLogsFor] = useState<Integracao | null>(null);
  const [logs, setLogs] = useState<IntegracaoLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [formData, setFormData] = useState({ nome: "", tipo: "finance", urlBase: "", apiKey: "", ativo: true });

  useEffect(() => { loadIntegracoes(); }, []);

  const loadIntegracoes = async () => {
    setIsLoading(true);
    try {
      setIntegracoes(await integracaoService.list());
    } catch (error) {
      console.error("Erro ao carregar integrações:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        nome: formData.nome,
        tipo: formData.tipo,
        urlBase: formData.urlBase,
        apiKey: formData.apiKey || null,
        ativo: formData.ativo,
      };
      if (editing) {
        await integracaoService.update(editing.id, payload);
        setMessage({ type: "success", text: "Integração atualizada com sucesso!" });
      } else {
        await integracaoService.create(payload);
        setMessage({ type: "success", text: "Integração criada com sucesso!" });
      }
      closeModal();
      loadIntegracoes();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao salvar integração" });
    }
  };

  const handleEdit = (integracao: Integracao) => {
    setEditing(integracao);
    setFormData({
      nome: integracao.nome,
      tipo: integracao.tipo,
      urlBase: integracao.urlBase,
      apiKey: integracao.apiKey || "",
      ativo: integracao.ativo,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Deseja remover esta integração?")) return;
    try {
      await integracaoService.remove(id);
      setMessage({ type: "success", text: "Integração removida!" });
      loadIntegracoes();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao remover" });
    }
  };

  const handleTest = async (id: string) => {
    setTestingId(id);
    setMessage(null);
    try {
      const result = await integracaoService.test(id);
      setMessage({
        type: result.ok ? "success" : "error",
        text: result.ok ? "Conexão estabelecida com sucesso!" : `Falha na conexão: ${result.erro || "sem resposta"}`,
      });
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao testar conexão" });
    } finally {
      setTestingId(null);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    setMessage(null);
    try {
      const result = await integracaoService.sync();
      setMessage({
        type: "success",
        text: `Sincronização concluída: ${result.enviadas} enviadas, ${result.erros} erros, ${result.puladas} puladas.`,
      });
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao sincronizar" });
    } finally {
      setIsSyncing(false);
    }
  };

  const openLogs = async (integracao: Integracao) => {
    setLogsFor(integracao);
    setLogsLoading(true);
    try {
      const result = await integracaoService.logs(integracao.id, { limit: 50 });
      setLogs(result.data);
    } catch (error) {
      console.error("Erro ao carregar logs:", error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleRetry = async (log: IntegracaoLog) => {
    if (!logsFor) return;
    try {
      await integracaoService.retry(logsFor.id, log.id);
      setMessage({ type: "success", text: "Reenvio disparado!" });
      openLogs(logsFor);
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao reenviar" });
    }
  };

  const openNewModal = () => {
    setEditing(null);
    setFormData({ nome: "", tipo: "finance", urlBase: "", apiKey: "", ativo: true });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Plug className="w-5 h-5 text-blue-600" /> Integrações Externas
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Conecte o Folio a outros sistemas. Faturas emitidas são enviadas automaticamente para as integrações do tipo "finance".
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSync} disabled={isSyncing} className="flex items-center gap-2 px-4 py-2 btn btn-outline disabled:opacity-50">
            {isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Sincronizar agora
          </button>
          <button onClick={openNewModal} className="flex items-center gap-2 px-4 py-2 btn btn-primary">
            <Plus className="w-4 h-4" /> Nova Integração
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>
        ) : integracoes.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Plug className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Nenhuma integração cadastrada. Crie a primeira para enviar faturas ao Finance.
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Destino</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {integracoes.map((integracao) => (
                <tr key={integracao.id} className="hover:bg-muted/50">
                  <td className="px-4 py-4 font-medium text-foreground">{integracao.nome}</td>
                  <td className="px-4 py-4 text-muted-foreground">{TIPO_LABELS[integracao.tipo] || integracao.tipo}</td>
                  <td className="px-4 py-4 text-muted-foreground text-sm">{integracao.urlBase}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${integracao.ativo ? "bg-green-500/20 text-green-700 dark:text-green-400" : "bg-red-500/20 text-red-700 dark:text-red-400"}`}>
                      {integracao.ativo ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleTest(integracao.id)} title="Testar conexão" className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-500/10 rounded-lg">
                        {testingId === integracao.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                      </button>
                      <button onClick={() => openLogs(integracao)} title="Ver logs" className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-500/10 rounded-lg">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleEdit(integracao)} className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(integracao.id)} className="p-2 text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {/* Modal de criação/edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">{editing ? "Editar Integração" : "Nova Integração"}</h3>
              <button onClick={closeModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nome *</label>
                <input type="text" value={formData.nome} onChange={(e) => setFormData(p => ({ ...p, nome: e.target.value }))} required placeholder="Ex: Finance Pessoal"
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Tipo *</label>
                <select value={formData.tipo} onChange={(e) => setFormData(p => ({ ...p, tipo: e.target.value }))}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground">
                  <option value="finance">Finance (contas a receber)</option>
                  <option value="zooy">Zooy OS (resumo de faturas)</option>
                  <option value="webhook">Webhook genérico</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">URL base do destino *</label>
                <input type="text" value={formData.urlBase} onChange={(e) => setFormData(p => ({ ...p, urlBase: e.target.value }))} required placeholder="http://finance-api-dev:3003"
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                <p className="text-xs text-muted-foreground mt-1">Para "finance", informe a URL base da API do Finance (sem /api).</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">API Key</label>
                <input type="text" value={formData.apiKey} onChange={(e) => setFormData(p => ({ ...p, apiKey: e.target.value }))} placeholder="Chave de integração"
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                <input type="checkbox" checked={formData.ativo} onChange={(e) => setFormData(p => ({ ...p, ativo: e.target.checked }))} />
                Integração ativa
              </label>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeModal} className="flex-1 py-2 btn btn-outline">Cancelar</button>
                <button type="submit" className="flex-1 py-2 btn btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Painel de logs */}
      {logsFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold text-foreground">Logs — {logsFor.nome}</h3>
              <button onClick={() => setLogsFor(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {logsLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>
              ) : logs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">Nenhum log registrado ainda.</div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => (
                    <div key={log.id} className="p-3 rounded-lg border border-border bg-muted/20">
                      <div className="flex items-center gap-2">
                        {log.status === "sucesso" ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : log.status === "erro" ? (
                          <AlertCircle className="w-4 h-4 text-red-500" />
                        ) : (
                          <Loader2 className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="font-medium text-sm text-foreground">{log.tipoEvento}</span>
                        <span className="text-xs text-muted-foreground">{new Date(log.createdAt).toLocaleString("pt-BR")}</span>
                        {log.status === "erro" && (
                          <button onClick={() => handleRetry(log)} className="ml-auto flex items-center gap-1 text-xs text-blue-600 hover:text-blue-500">
                            <RotateCcw className="w-3 h-3" /> Reenviar
                          </button>
                        )}
                      </div>
                      {log.erroMensagem && (
                        <p className="text-xs text-red-600 mt-1 ml-6">{log.erroMensagem}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
